import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { money, fmtDate, normalizeStatus, STATUS_LABELS } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { Plus, ClipboardText, Receipt, Factory, CheckCircle, TrendUp, CurrencyDollar, ArrowRight } from "@phosphor-icons/react";

const KPI = ({ label, value, icon: Icon, accent, testid }) => (
  <div className="surface p-5" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div>
        <div className="overline">{label}</div>
        <div className="font-display text-4xl mt-2 text-slate-900 leading-none tabular-nums">{value}</div>
      </div>
      <div className="w-9 h-9 flex items-center justify-center border hairline" style={{ color: accent }}>
        <Icon size={20} weight="duotone" />
      </div>
    </div>
  </div>
);

export default function Dashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      <PageHeader
        title="Command Center"
        subtitle="Shop overview"
        testid="dashboard-header"
        actions={
          <>
            <Link to="/customers" className="btn-ghost" data-testid="quick-add-customer">
              <Plus size={16} className="inline mr-1" /> Customer
            </Link>
            <Link to="/invoices" className="btn-primary" data-testid="quick-new-invoice">
              <Plus size={16} className="inline mr-1" /> New Invoice
            </Link>
          </>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 stagger" data-testid="kpi-grid">
        <KPI label="Active Orders" value={stats?.active_orders ?? "—"} icon={ClipboardText} accent="#22D3EE" testid="kpi-active" />
        <KPI label="In Production" value={stats?.in_production ?? "—"} icon={Factory} accent="#F59E0B" testid="kpi-in-production" />
        <KPI label="Fulfilled" value={stats?.fulfilled_total ?? "—"} icon={CheckCircle} accent="#10B981" testid="kpi-fulfilled" />
        <KPI label="Unpaid Invoices" value={stats?.unpaid_invoices ?? "—"} icon={Receipt} accent="#EF4444" testid="kpi-unpaid" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8">
        <div className="lg:col-span-2 surface" data-testid="recent-orders-card">
          <div className="flex items-center justify-between px-5 py-4 border-b hairline">
            <div>
              <div className="overline">Recent</div>
              <h2 className="text-lg text-slate-900 font-semibold">Work Orders</h2>
            </div>
            <Link to="/work-orders" className="text-sm text-slate-600 hover:text-slate-900 flex items-center gap-1">
              View all <ArrowRight size={14} />
            </Link>
          </div>
          {(!stats?.recent_orders || stats.recent_orders.length === 0) ? (
            <EmptyRow label="No work orders yet" />
          ) : (
            <table className="data-table">
              <thead>
                <tr><th>Number</th><th>Customer</th><th>Type</th><th>Status</th><th>Deadline</th></tr>
              </thead>
              <tbody>
                {stats.recent_orders.map((o) => (
                  <tr key={o.id} onClick={() => (window.location.href = `/work-orders/${o.id}`)} data-testid={`dash-wo-${o.id}`}>
                    <td className="font-mono text-slate-900 font-medium">{o.number}</td>
                    <td>{o.customer_name}</td>
                    <td><span className={`chip ${o.job_type}`}>{o.job_type}</span></td>
                    <td><span className={`chip ${normalizeStatus(o.status)}`}>{STATUS_LABELS[normalizeStatus(o.status)] || o.status}</span></td>
                    <td className="font-mono">{fmtDate(o.deadline)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="surface" data-testid="outstanding-card">
          <div className="px-5 py-4 border-b hairline">
            <div className="overline">Cash</div>
            <h2 className="text-lg text-slate-900 font-semibold">Outstanding</h2>
          </div>
          <div className="p-5">
            <div className="flex items-baseline gap-2">
              <TrendUp size={20} className="text-amber-400" />
              <div className="font-display text-5xl text-slate-900 tabular-nums">{money(stats?.outstanding || 0)}</div>
            </div>
            <p className="text-sm text-slate-500 mt-3">Total balance across unpaid and partial invoices.</p>
            <div className="mt-6">
              <Link to="/invoices" className="btn-ghost text-sm inline-flex items-center gap-2" data-testid="view-invoices-btn">
                Manage invoices <ArrowRight size={14} />
              </Link>
            </div>
          </div>

          <div className="border-t hairline px-5 py-4">
            <div className="overline mb-3">Recent Invoices</div>
            {(!stats?.recent_invoices || stats.recent_invoices.length === 0) ? (
              <div className="text-sm text-slate-400">None yet.</div>
            ) : (
              <div className="flex flex-col gap-2">
                {stats.recent_invoices.slice(0, 4).map((i) => (
                  <Link key={i.id} to={`/invoices/${i.id}`} className="flex items-center justify-between text-sm hover:text-slate-900 text-slate-600">
                    <span className="font-mono">{i.number}</span>
                    <span className="truncate max-w-[120px] mx-2 text-slate-500">{i.customer_name}</span>
                    <span className={`chip ${i.payment_status}`}>{i.payment_status}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const EmptyRow = ({ label }) => (
  <div className="p-8 text-center text-slate-400 text-sm">{label}</div>
);
